import { useEffect, useRef, useState } from 'react'
import { api, ErroApi } from '../lib/api'
import { Marca } from '../componentes/Marca'
import './criar-conta.css'

/**
 * Cadastro em três passos.
 *
 * Um formulário só, com nove campos, é o que faz metade das pessoas
 * desistirem antes de terminar. Em três passos cada tela pede pouco e
 * o progresso é visível.
 *
 * O endereço é conferido enquanto se digita, contra a MESMA função que
 * o banco usa (`app.slug_livre`) — inclusive a lista de reservados.
 * Descobrir que o endereço está ocupado só ao enviar é o erro mais
 * frustrante de um cadastro.
 */

const VERTICAIS = [
  { id: 'fisioterapia', rotulo: 'Fisioterapia e reabilitação' },
  { id: 'odontologia', rotulo: 'Odontologia' },
  { id: 'estetica', rotulo: 'Estética e bem-estar' },
  { id: 'medica', rotulo: 'Clínica médica' },
] as const

/** "Clínica Vitalis" → "clinica-vitalis" */
function sugerirSlug(nome: string): string {
  return nome
    .normalize('NFD').replace(/[̀-ͯ]/g, '')
    .toLowerCase().replace(/[^a-z0-9]+/g, '-')
    .replace(/^-+|-+$/g, '').slice(0, 40)
}

export function CriarConta({ plano, aoEntrar, aoVoltar }: {
  plano?: string
  aoEntrar(): void
  aoVoltar(): void
}) {
  const [passo, setPasso] = useState(1)
  const [nome, setNome] = useState('')
  const [email, setEmail] = useState('')
  const [senha, setSenha] = useState('')
  const [clinica, setClinica] = useState('')
  const [slug, setSlug] = useState('')
  const [slugTocado, setSlugTocado] = useState(false)
  const [vertical, setVertical] = useState<string>('fisioterapia')
  const [slugLivre, setSlugLivre] = useState<boolean | null>(null)
  const [erro, setErro] = useState<string | null>(null)
  const [enviando, setEnviando] = useState(false)
  const [pronto, setPronto] = useState(false)
  const timer = useRef<number | null>(null)

  // Enquanto ninguém editar o endereço à mão, ele segue o nome.
  useEffect(() => {
    if (!slugTocado) setSlug(sugerirSlug(clinica))
  }, [clinica, slugTocado])

  useEffect(() => {
    if (slug.length < 4) { setSlugLivre(null); return }
    const c = new AbortController()
    if (timer.current) window.clearTimeout(timer.current)
    timer.current = window.setTimeout(async () => {
      try {
        const r = await api<{ livre: boolean }>(
          `/v1/conta/slug/${encodeURIComponent(slug)}`, { sinal: c.signal })
        setSlugLivre(r.livre)
      } catch { setSlugLivre(null) }
    }, 300)
    return () => { c.abort(); if (timer.current) window.clearTimeout(timer.current) }
  }, [slug])

  const senhaCurta = senha.length > 0 && senha.length < 10

  async function submeter(e: React.FormEvent) {
    e.preventDefault()
    setErro(null); setEnviando(true)
    try {
      await api('/v1/conta', {
        metodo: 'POST',
        corpo: {
          email: email.trim(), senha, nome: nome.trim(),
          clinica: clinica.trim(), slug, vertical,
          ...(plano ? { plano } : {}),
        },
      })
      setPronto(true)
    } catch (err) {
      setErro(err instanceof ErroApi && err.codigo === 'SLUG_OCUPADO'
        ? 'Esse endereço acabou de ser ocupado. Escolha outro.'
        : err instanceof ErroApi ? err.message : 'Não foi possível criar a conta.')
      setEnviando(false)
      if (err instanceof ErroApi && err.codigo === 'SLUG_OCUPADO') setPasso(2)
    }
  }

  if (pronto) {
    return (
      <main className="cc">
        <div className="cc__caixa">
          <Marca tamanho={30} />
          <h1>Confira seu e-mail</h1>
          <p>
            Mandamos um link para <strong>{email}</strong>. Ele confirma que o
            endereço é seu e libera o acesso.
          </p>
          <p className="ajuda">
            Não chegou em alguns minutos? Veja no spam — e se não estiver lá,
            entre e peça outro.
          </p>
          <button className="botao" onClick={aoEntrar}>Ir para o login</button>
        </div>
      </main>
    )
  }

  return (
    <main className="cc">
      <form className="cc__caixa" onSubmit={submeter}>
        {/* Em linha com a marca, não sobreposto. O `base.css` dá
            `min-height: 44px` a todo botão (alvo de toque da WCAG), e
            com `position:absolute` isso fazia o link cobrir o logo. */}
        <div className="cc__cab">
          <Marca tamanho={28} />
          <button type="button" className="cc__voltar" onClick={aoVoltar}>← Início</button>
        </div>

        <div className="cc__passos" aria-label={`Passo ${passo} de 3`}>
          {[1, 2, 3].map((n) => (
            <i key={n} className={n <= passo ? 'is-feito' : ''} />
          ))}
        </div>

        {erro && <div className="aviso aviso--erro" role="alert">{erro}</div>}

        {passo === 1 && (
          <>
            <div>
              <h1>Vamos começar</h1>
              <p className="ajuda">Primeiro, quem é você.</p>
            </div>
            <div className="campo">
              <label htmlFor="cc-nome">Seu nome</label>
              <input id="cc-nome" className="entrada" required autoFocus
                     value={nome} onChange={(e) => setNome(e.target.value)} />
            </div>
            <div className="campo">
              <label htmlFor="cc-email">E-mail</label>
              <input id="cc-email" className="entrada" type="email" required
                     autoComplete="username" inputMode="email"
                     value={email} onChange={(e) => setEmail(e.target.value)} />
            </div>
            <div className="campo">
              <label htmlFor="cc-senha">Senha</label>
              <input id="cc-senha" className="entrada" type="password" required
                     autoComplete="new-password" minLength={10}
                     aria-invalid={senhaCurta}
                     value={senha} onChange={(e) => setSenha(e.target.value)} />
              <span className={senhaCurta ? 'ajuda ajuda--erro' : 'ajuda'}>
                {senhaCurta
                  ? `${senha.length} de 10 caracteres, no mínimo.`
                  : 'Mínimo de 10 caracteres.'}
              </span>
            </div>
            <button type="button" className="botao"
                    disabled={!nome.trim() || !email.trim() || senha.length < 10}
                    onClick={() => setPasso(2)}>Continuar</button>
          </>
        )}

        {passo === 2 && (
          <>
            <div>
              <h1>Sua clínica</h1>
              <p className="ajuda">É por este endereço que sua equipe entra.</p>
            </div>
            <div className="campo">
              <label htmlFor="cc-clinica">Nome da clínica</label>
              <input id="cc-clinica" className="entrada" required autoFocus
                     value={clinica} onChange={(e) => setClinica(e.target.value)} />
            </div>
            <div className="campo">
              <label htmlFor="cc-slug">Endereço</label>
              <div className="cc__url">
                <span>app.clinuma.com.br/</span>
                <input id="cc-slug" className="entrada" required
                       value={slug}
                       onChange={(e) => {
                         setSlugTocado(true)
                         setSlug(e.target.value.toLowerCase().replace(/[^a-z0-9-]/g, ''))
                       }} />
              </div>
              <span className={
                slugLivre === false ? 'ajuda ajuda--erro'
                : slugLivre === true ? 'ajuda ajuda--ok' : 'ajuda'}>
                {slug.length < 4 ? 'Pelo menos 4 letras.'
                  : slugLivre === true ? '✓ disponível'
                  : slugLivre === false ? 'Já em uso ou reservado. Tente outro.'
                  : 'Conferindo…'}
              </span>
            </div>
            <div className="cc__dupla">
              <button type="button" className="botao botao--fantasma"
                      onClick={() => setPasso(1)}>Voltar</button>
              <button type="button" className="botao"
                      disabled={!clinica.trim() || slugLivre !== true}
                      onClick={() => setPasso(3)}>Continuar</button>
            </div>
          </>
        )}

        {passo === 3 && (
          <>
            <div>
              <h1>O que sua clínica faz</h1>
              <p className="ajuda">
                Só define o que já vem pronto no começo. Você pode atender de tudo.
              </p>
            </div>
            <div className="cc__opcoes">
              {VERTICAIS.map((v) => (
                <label key={v.id} className={vertical === v.id ? 'is-escolhida' : ''}>
                  <input type="radio" name="vertical" value={v.id}
                         checked={vertical === v.id}
                         onChange={() => setVertical(v.id)} />
                  <span>{v.rotulo}</span>
                </label>
              ))}
            </div>
            <p className="ajuda">
              14 dias grátis. Sem cartão. Ao continuar você aceita os Termos e a
              Política de Privacidade.
            </p>
            <div className="cc__dupla">
              <button type="button" className="botao botao--fantasma"
                      onClick={() => setPasso(2)}>Voltar</button>
              <button className="botao" type="submit" disabled={enviando}>
                {enviando ? 'Criando…' : 'Criar minha clínica'}
              </button>
            </div>
          </>
        )}
      </form>
    </main>
  )
}
